import {
  getObjectByPath,
  hasTypeParams,
} from '../applyMenuConfigChanges/utils';
import { pathToTreeKeys } from '../applyMenuConfigChanges/deleteMembers';
import { traverseTree } from '../traverseTree';
import { toUrlFormat } from '../treeVisitors';
import { isPrimitiveType } from '../../client/components/TypeDetails/NestedTable/utils';
import { isCategory } from '../../client/components/MenuEditor/utils';
import { isNode } from '../treeVisitors/helpers';
import { extractType } from '../../client/components/TypeDetails/utils';
import { escapeRegex, getLastOccurenceOfType } from './utils';
import { isEqual } from 'lodash';
import { deepCopy } from '../objectUtils';
import {
  ComplexType,
  isLegacyType,
  LegacyComplexType,
  LegacySingleType,
  Type,
  TypeInServiceJson,
  TypeParameter,
} from '../../client/components/TypeDetails/typeParser';

const shouldAddDisplayNameToType = (
  oldType,
  updatedType,
  isNameCollision: boolean,
  hasDisplayName,
) => isNameCollision && !isEqual(oldType, updatedType) && !hasDisplayName;

const shouldNotReplaceType = (path, urlFormatType, pathIndexInType) =>
  pathIndexInType === -1 ||
  ((!urlFormatType[pathIndexInType + path.length] ||
    urlFormatType[pathIndexInType + path.length] !== '.') &&
    path !== urlFormatType);

export const getNewType = (
  path: string,
  newType: string,
  type: string = '',
) => {
  const urlFormatType = type.toLowerCase().replace(/ /g, '-');
  const pathIndexInType = urlFormatType.lastIndexOf(path);
  if (shouldNotReplaceType(path, urlFormatType, pathIndexInType)) {
    return type;
  }
  const pathDisplayName = type.substring(
    pathIndexInType,
    path.length + pathIndexInType,
  );
  const typeToReplace = path.split('.').pop();

  const indexOfTypeInPath = path.lastIndexOf(typeToReplace);
  const typeToReplaceDisplayName = pathDisplayName.substring(
    indexOfTypeInPath,
    typeToReplace.length + indexOfTypeInPath,
  );

  const lastOccurenceOfType = getLastOccurenceOfType(
    type,
    typeToReplaceDisplayName,
  );
  const newTypeToDisplay =
    type.substring(0, lastOccurenceOfType) +
    newType +
    type.substring(lastOccurenceOfType + typeToReplaceDisplayName.length);

  return newTypeToDisplay;
};

const createTypeParams = (
  path: string,
  newType,
  type: LegacyComplexType | ComplexType,
): TypeParameter[] | LegacySingleType[] =>
  hasTypeParams(type)
    ? createNewArrayOfTypes(path, newType, type.typeParams)
    : [];

const createNewArrayOfTypes = (
  path,
  newType,
  types: Type[] | TypeParameter[] | LegacySingleType[],
) => {
  if (isLegacyType(types)) {
    return (types as LegacySingleType[]).map((_type) => {
      if (hasTypeParams(_type)) {
        return buildNewType(path, newType, _type);
      }
      return isPrimitiveType(_type) || typeof _type !== 'string'
        ? _type
        : getNewType(path, newType, _type);
    });
  }

  return (types as (TypeParameter | Type)[]).map((_type) => {
    if (_type.complexType || _type.referenceType) {
      return buildNewType(path, newType, _type);
    }

    if (_type.nativeType) {
      return _type;
    }

    const _typeAsTypeParameter = _type as TypeParameter;
    if (_typeAsTypeParameter.unionType) {
      return {
        unionType: {
          type: createNewArrayOfTypes(
            path,
            newType,
            _typeAsTypeParameter.unionType.type,
          ),
        },
      };
    }
  });
};

export const buildNewType = (
  path: string,
  newType,
  type: LegacySingleType | Type = '',
  isForMovingMembersToCategory: boolean = false,
): LegacySingleType | Type => {
  if (isForMovingMembersToCategory && Array.isArray(type)) {
    // TODO TODO TODO this `if` is hacky! REMOVE THIS LINE AFTER FIXING WAM-7537
    return type;
  }

  if (isLegacyType(type)) {
    if (isPrimitiveType(type)) {
      return type;
    }
    if (typeof type === 'string') {
      return getNewType(path, newType, type);
    }
    if (Array.isArray(type)) {
      return createNewArrayOfTypes(path, newType, type);
    }
    return {
      ...type,
      typeParams: createTypeParams(path, newType, type),
    } as LegacySingleType;
  }

  const typeAsNewTypeSchema = type as Type;

  const buildNewReferenceType = (
    referenceType: string,
    typeDisplayName?: string,
  ): { referenceType: string; displayName?: string } => {
    const newReferenceTypeString = getNewType(path, newType, referenceType);
    const shouldAddDisplayName =
      !isForMovingMembersToCategory && // when moving members to different category, the member was not renamed, so displayName is not needed
      !isEqual(referenceType, newReferenceTypeString);
    const returnObject: { referenceType: string; displayName?: string } = {
      referenceType: newReferenceTypeString,
      displayName: typeDisplayName,
    };
    if (shouldAddDisplayName) {
      returnObject.displayName = referenceType;
    }
    return returnObject;
  };

  if (typeAsNewTypeSchema.referenceType) {
    return buildNewReferenceType(
      typeAsNewTypeSchema.referenceType,
      typeAsNewTypeSchema.displayName,
    );
  }

  if (typeAsNewTypeSchema.complexType) {
    const shouldAddNativeType: boolean = !!typeAsNewTypeSchema.complexType
      .nativeType;

    /*
    Prefix of the param, e.g Array, Promise.
    The prefix is either something native, or a reference to a message which receives a type parameter.
     */
    const prefixType = shouldAddNativeType
      ? { nativeType: typeAsNewTypeSchema.complexType.nativeType }
      : buildNewReferenceType(
          type.complexType.referenceType,
          typeAsNewTypeSchema.complexType?.displayName,
        );
    return {
      complexType: {
        ...prefixType,
        typeParams: createTypeParams(
          path,
          newType,
          typeAsNewTypeSchema.complexType,
        ),
      },
    } as { complexType: ComplexType };
  }

  // type is a native type
  return typeAsNewTypeSchema;
};

const updateType = (
  path,
  newType,
  updateAt,
  subMembersPropName,
  isNameCollision,
  isForMovingMembersToCategory,
) => (node) => {
  if (node && node[updateAt]) {
    node[updateAt].forEach((member) => {
      updateReturnTypeIfNeeded(member, path, newType, isNameCollision);
      updateMixedInFromIfNeeded(
        member,
        path,
        newType,
        isForMovingMembersToCategory,
      );
      updateOperationNotesIfNeeded(
        member,
        path,
        newType,
        isForMovingMembersToCategory,
      );
      updateSubmembers(
        member,
        path,
        newType,
        subMembersPropName,
        isNameCollision,
        isForMovingMembersToCategory,
      );
    });
  }
};

const updateMemberOf = (path, newType, isForMovingMembersToCategory) => (
  node,
) => {
  if (isNode(node) && node.data.memberOf && !isForMovingMembersToCategory) {
    const type = node.data.memberOf;
    node.data.memberOf = buildNewType(path, newType, type);
  }
};

const updatePropertyType = (
  path,
  newType,
  isNameCollision,
  isForMovingMembersToCategory,
) => (node) => {
  if (node && node.properties) {
    node.properties.forEach((property) => {
      updateMixedInFromIfNeeded(
        property,
        path,
        newType,
        isForMovingMembersToCategory,
      );
      if (Array.isArray(property.type)) {
        if (!isForMovingMembersToCategory || !isLegacyType(property.type)) {
          // TODO TODO TODO this `if` is hacky! REMOVE THIS LINE AFTER FIXING WAM-7537
          property.type.forEach((possibleType, i) => {
            const oldType = deepCopy(possibleType);
            property.type[i] = buildNewType(
              path,
              newType,
              possibleType,
              isForMovingMembersToCategory,
            );
            if (oldType.displayName) {
              property.type[i].displayName = oldType.displayName;
            } else if (
              shouldAddDisplayNameToType(
                oldType,
                property.type[i],
                isNameCollision,
                property.type[i].displayName,
              )
            ) {
              property.type[i].displayName = extractTypeDisplayName(oldType);
            }
          });
        }
      } else {
        const oldType = deepCopy(property.type);
        property.type = buildNewType(
          path,
          newType,
          property.type,
          isForMovingMembersToCategory,
        );
        if (
          shouldAddDisplayNameToType(
            oldType,
            property.type,
            isNameCollision,
            property.typeDisplayName,
          )
        ) {
          property.typeDisplayName = extractTypeDisplayName(oldType);
        }
      }
    });
  }
};

const updateMdLinks = (
  path,
  newType,
  updateAt,
  isForMovingMembersToCategory,
) => (node) => {
  if (node && node[updateAt] && !isForMovingMembersToCategory) {
    node[updateAt].forEach((prop) => {
      updateLinksIfNeeded(prop.docs, 'links', path, newType);
      updateLinksIfNeeded(prop.docs, 'description', path, newType);
    });
  }
};

const updateRelatedGuidesContent = (
  path,
  newType,
  isForMovingMembersToCategory,
) => (node) => {
  if (node && node.relatedGuides && !isForMovingMembersToCategory) {
    node.relatedGuides.forEach((guide) => {
      if (guide.content) {
        guide.content = getMdLinkWithNewCategory(guide.content, path, newType);
      }
    });
  }
};

const updateMixes = (path, newType, isForMovingMembersToCategory) => (node) => {
  if (node && node.mixes && !isForMovingMembersToCategory) {
    node.mixes = node.mixes.map((type) => {
      return getNewType(path, newType, type);
    });
  }
};

const updateLinksIfNeeded = (docs, updateAt, path, newType) => {
  if (docs && docs[updateAt]) {
    docs[updateAt] = docs[updateAt].map((link) => {
      return getMdLinkWithNewCategory(link, path, newType);
    });
  }
};

const extractTypeDisplayName = (type): string => {
  if (typeof type === 'string') {
    return type;
  }
  return extractTypeDisplayName(extractType(type));
};

const updateReturnTypeIfNeeded = (member, path, newType, isNameCollision) => {
  if (member.ret) {
    if (Array.isArray(member.ret.type)) {
      member.ret.type.forEach((possibleType, i) => {
        const oldType = deepCopy(possibleType);
        member.ret.type[i] = buildNewType(path, newType, possibleType);
        if (oldType.displayName) {
          member.ret.type[i].displayName = oldType.displayName;
        }
        if (
          isLegacyType(oldType) &&
          shouldAddDisplayNameToType(
            oldType,
            member.ret.type[i],
            isNameCollision,
            member.ret.type[i].displayName,
          )
        ) {
          member.ret.type[i].displayName = extractTypeDisplayName(oldType);
        }
      });
    } else {
      const oldType = deepCopy(member.ret.type);
      member.ret.type = buildNewType(path, newType, member.ret.type);
      if (
        shouldAddDisplayNameToType(
          oldType,
          member.ret.type,
          isNameCollision,
          member.ret.displayName,
        )
      ) {
        member.ret.displayName = extractTypeDisplayName(oldType);
      }
    }
  }
};

const shouldReplaceMixedInFrom = (operation, path) => {
  const [oldMemberOf, oldName] = path.split('.');
  const { memberOf, name } = operation.mixedInFrom;
  if (!oldName && toUrlFormat(memberOf) === oldMemberOf) {
    return true;
  }
  return toUrlFormat(memberOf) === oldMemberOf && toUrlFormat(name) === oldName;
};

const shouldReplaceOriginalMixedIn = (operation, path) => {
  if (!operation.mixedInFrom.original) {
    return false;
  }
  const [oldMemberOf, oldName] = path.split('.');
  const {
    original: { memberOf, name },
  } = operation.mixedInFrom;
  if (!oldName && toUrlFormat(memberOf) === oldMemberOf) {
    return true;
  }
  return toUrlFormat(memberOf) === oldMemberOf && toUrlFormat(name) === oldName;
};

const updateMixedInFromIfNeeded = (
  operation,
  path,
  newType,
  isForMovingMembersToCategory,
) => {
  if (
    operation.mixedInFrom &&
    operation.mixedInFrom.memberOf &&
    !isForMovingMembersToCategory
  ) {
    if (shouldReplaceMixedInFrom(operation, path)) {
      if (isCategory(path)) {
        operation.mixedInFrom.memberOf = newType;
      } else {
        operation.mixedInFrom.name = newType;
      }
    }
    if (shouldReplaceOriginalMixedIn(operation, path)) {
      if (isCategory(path)) {
        operation.mixedInFrom.original.memberOf = newType;
      } else {
        operation.mixedInFrom.original.name = newType;
      }
    }
  }
};

const updateOperationNotesIfNeeded = (
  operation,
  path,
  newType,
  isForMovingMembersToCategory,
) => {
  if (
    operation.extra &&
    operation.extra.notes &&
    !isForMovingMembersToCategory
  ) {
    operation.extra.notes = operation.extra.notes.map((note) =>
      getMdLinkWithNewCategory(note, path, newType),
    );
  }
};

const updateSubmembers = (
  member,
  path,
  newType: TypeInServiceJson,
  subMembersPropName,
  isNameCollision: boolean,
  isForMovingMembersToCategory: boolean,
): void => {
  member[subMembersPropName].forEach((param) => {
    updateParamDocIfNeeded(param, path, newType, isForMovingMembersToCategory);
    if (isNameCollision && isLegacyType(param.type) && !param.typeDisplayName) {
      const typeDisplayName = extractTypeDisplayName(param.type);
      if (!isPrimitiveType(typeDisplayName)) {
        param.typeDisplayName = typeDisplayName;
      }
    }
    if (Array.isArray(param.type)) {
      if (!isForMovingMembersToCategory || !isLegacyType(param.type)) {
        // TODO TODO TODO this `if` is hacky! REMOVE THIS LINE AFTER FIXING WAM-7537
        param.type = param.type.map((_type: LegacySingleType | Type) => {
          if (isLegacyType(_type)) {
            return buildNewType(
              path,
              newType,
              _type,
              isForMovingMembersToCategory,
            );
          }

          const typeAsNewTypeSchema = _type as Type;
          if (typeAsNewTypeSchema.nativeType) {
            return typeAsNewTypeSchema;
          }
          if (
            typeAsNewTypeSchema.referenceType ||
            typeAsNewTypeSchema.complexType
          ) {
            return buildNewType(
              path,
              newType,
              _type,
              isForMovingMembersToCategory,
            );
          }
        });
      }
    } else {
      param.type = buildNewType(
        path,
        newType,
        param.type,
        isForMovingMembersToCategory,
      );
    }
  });
};

/**
 * Updates a param's description if exists.
 * @param param
 * @param path
 * @param newType
 * @param isForMovingMembersToCategory
 */
const updateParamDocIfNeeded = (
  param,
  path: string,
  newType,
  isForMovingMembersToCategory,
): void => {
  if (param && param.doc && !isForMovingMembersToCategory) {
    param.doc = getMdLinkWithNewCategory(param.doc, path, newType);
  }
};

export const getMdLinkWithNewCategory = (link, path, newType) => {
  const typeToReplace = toUrlFormat(path.split('.').pop());
  const regexMdLinks = `\\]\\(${escapeRegex(path)}[.|\\#]`;
  const match = new RegExp(regexMdLinks, 'gmi');
  const newLink = link.replace(match, (_match) => {
    return _match.toLowerCase().replace(typeToReplace, newType);
  });
  return newLink;
};

const updateTypes = (
  tree,
  path,
  newType,
  isNameCollision: boolean,
  isForMovingMembersToCategory: boolean,
) => {
  traverseTree(tree, [
    updateType(
      path,
      newType,
      'operations',
      'params',
      isNameCollision,
      isForMovingMembersToCategory,
    ),
    updateType(
      path,
      newType,
      'clientEvents',
      'params',
      isNameCollision,
      isForMovingMembersToCategory,
    ),
    updateType(
      path,
      newType,
      'messages',
      'members',
      isNameCollision,
      isForMovingMembersToCategory,
    ),
    updateType(
      path,
      newType,
      'callbacks',
      'params',
      isNameCollision,
      isForMovingMembersToCategory,
    ),
    updatePropertyType(
      path,
      newType,
      isNameCollision,
      isForMovingMembersToCategory,
    ),
    updateMdLinks(path, newType, 'properties', isForMovingMembersToCategory),
    updateMdLinks(path, newType, 'operations', isForMovingMembersToCategory),
    updateMdLinks(path, newType, 'clientEvents', isForMovingMembersToCategory),
    updateRelatedGuidesContent(path, newType, isForMovingMembersToCategory),
    updateMixes(path, newType, isForMovingMembersToCategory),
    updateMemberOf(path, newType, isForMovingMembersToCategory),
  ]);
};

const changeUrlFormat = (parentNode, objectToChange, newKey, oldKey) => {
  objectToChange.data.urlFormat = newKey;
  parentNode[`/${newKey}`] = objectToChange;
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete parentNode[`/${oldKey}`];
};

export const updateTree = (
  docsTree,
  path: string,
  newUrlFormat: string,
  isNameCollision = false,
  isForMovingMembersToCategory = false, // this parameter is `true` when `updateTree` is triggered from moveMembersToCategories visitor
) => {
  const objectToChange = getObjectByPath(docsTree, pathToTreeKeys(path));
  const parentNodeKeys = pathToTreeKeys(path).slice(0, -1);
  const parentNode = getObjectByPath(docsTree, parentNodeKeys);

  if (!isForMovingMembersToCategory) {
    changeUrlFormat(
      parentNode,
      objectToChange,
      newUrlFormat,
      objectToChange.data.urlFormat,
    );
  }

  updateTypes(
    docsTree,
    path,
    toUrlFormat(newUrlFormat),
    isNameCollision,
    isForMovingMembersToCategory,
  );
};
